<template>
  <RevCard class="flex-1 p-24">
    <RevTag
      v-if="!taxIdentificationNumbers.length"
      class="mb-8"
      :label="i18n(translations.toDoLabel)"
      variant="warning"
    />
    <WithSkeletonLoader :is-loading="pending">
      <div class="flex items-baseline">
        <div class="grow">
          <h2 class="heading-2 mb-8">
            {{ i18n(translations.taxIdentificationNumber) }}
          </h2>
          <div class="body-1 mb-4">
            {{ i18n(translations.description) }}
            <RevLink target="_blank" :to="i18n(translations.helpLinkUrl)">
              {{ i18n(translations.helpLinkText) }}
            </RevLink>
          </div>
        </div>
      </div>
      <RevTable
        v-if="taxIdentificationNumbers.length"
        class="mt-8"
        :collection="sortedTaxIdentificationNumbers"
        :columns="taxIdentificationNumberColumns"
      >
        <template v-if="isMainTinAdded" #body-isMain="{ item }">
          <RevTag
            v-if="item.isMain"
            class="md:mx-auto"
            :data-test="`${item.country}-tag`"
            :label="i18n(translations.mainLocation)"
            variant="success"
          />
          <!-- Here we need to explicitly add a non-breaking space to prevent the table from displaying the value of item.isMain when it's false -->
          <template v-else>&nbsp;</template>
        </template>
        <template #body-country="{ item }">
          <div class="flex items-center">
            <Flag class="my-8 mr-8" :country-code="item.country" size="xl" />
            <div class="items-baseline">
              <span class="mr-8">
                {{ i18n.country(item.country) }}
              </span>
            </div>
          </div>
        </template>
        <template #body-taxIdentificationNumber="{ item }">
          {{ item.taxIdentificationNumber }}
        </template>
        <template #body-actions="{ item }">
          <RevButtonIcon
            class="mr-8"
            full-width="never"
            :icon="IconEdit"
            variant="secondary"
            @click="handleEditTaxIdentificationNumber(item)"
          />
          <RevButtonIcon
            :aria-label="i18n(translations.deleteTinButton)"
            class="mr-8"
            full-width="never"
            :icon="IconTrash"
            :loading="isDeleting"
            variant="secondary"
            @click="handleDeleteTaxIdentificationNumber(item.id)"
          />
        </template>
      </RevTable>
      <RevButton
        id="addTaxIdentificationNumberButton"
        class="mt-16"
        variant="primary"
        @click="addTaxIdentificationNumber"
      >
        {{ formattedAddTinButtonLabel }}
      </RevButton>

      <TaxIdentificationNumberModal
        :already-added-countries
        :tax-identification-number-country-options
        :tax-identification-number-to-edit="selectedTaxIdentificationNumber"
        @tin-updated="refresh"
      />
    </WithSkeletonLoader>
  </RevCard>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { computed, ref } from 'vue'

import { type Country, sellerProfileAPI } from '@backmarket/http-api'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useHttpLazyFetch } from '@backmarket/nuxt-module-http/useHttpLazyFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import { RevButton } from '@ds/components/Button'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevCard } from '@ds/components/Card'
import { RevLink } from '@ds/components/Link'
import { openModal } from '@ds/components/ModalBase'
import { RevTable } from '@ds/components/Table'
import { RevTag } from '@ds/components/Tag'
import { IconEdit } from '@ds/icons/IconEdit'
import { IconTrash } from '@ds/icons/IconTrash'

import { MODAL_NAMES } from '../../../../Profile.constants'
import WithSkeletonLoader from '../../../../components/WithSkeletonLoader/WithSkeletonLoader.vue'

import translations from './TaxIdentificationNumber.translations'
import TaxIdentificationNumberModal from './components/TaxIdentificationNumberModal.vue'

const i18n = useI18n()
const { openErrorToast, openSuccessToast } = useTheToast()

defineProps<{
  taxIdentificationNumberCountryOptions: Country[]
}>()

const selectedTaxIdentificationNumber =
  ref<sellerProfileAPI.GetTaxIdentificationNumbersItem | null>(null)
const isDeleting = ref(false)

const {
  data: taxIdentificationNumbersPayload,
  error,
  pending,
  refresh,
} = await useHttpLazyFetch(sellerProfileAPI.getTaxIdentificationNumbers)

const taxIdentificationNumbers = computed(
  () => taxIdentificationNumbersPayload.value?.results ?? [],
)

if (error.value) {
  openErrorToast({
    content: getApiErrorMessage(error.value as unknown as Error),
  })
}

const sortedTaxIdentificationNumbers = computed(() => {
  return [...taxIdentificationNumbers.value].sort((a, b) => {
    return Number(b.isMain) - Number(a.isMain)
  })
})

const isMainTinAdded = computed(() => {
  return sortedTaxIdentificationNumbers.value.some((tin) => tin.isMain)
})

const taxIdentificationNumberColumns = computed(() => {
  return [
    ...insertIf(isMainTinAdded.value, [
      {
        key: 'isMain',
        label: '',
      },
    ]),
    {
      key: 'country',
      label: i18n(translations.countryOfIssue),
    },
    {
      key: 'taxIdentificationNumber',
      label: i18n(translations.taxIdentificationNumber),
    },
    {
      key: 'actions',
      label: '',
    },
  ]
})

const formattedAddTinButtonLabel = computed(() => {
  return taxIdentificationNumbers.value.length > 0
    ? i18n(translations.addNewTinButtonLabel)
    : i18n(translations.addTinButtonLabel)
})

const alreadyAddedCountries = computed(() => {
  return taxIdentificationNumbers.value.map(
    (
      taxIdentificationNumber: sellerProfileAPI.GetTaxIdentificationNumbersItem,
    ) => taxIdentificationNumber.country,
  )
})

function addTaxIdentificationNumber() {
  selectedTaxIdentificationNumber.value = null
  openModal(MODAL_NAMES.EDIT_TAX_IDENTIFICATION_NUMBER)
}

function handleEditTaxIdentificationNumber(
  taxIdentificationNumber: sellerProfileAPI.GetTaxIdentificationNumbersItem,
) {
  selectedTaxIdentificationNumber.value = taxIdentificationNumber
  openModal(MODAL_NAMES.EDIT_TAX_IDENTIFICATION_NUMBER)
}

async function handleDeleteTaxIdentificationNumber(
  taxIdentificationNumberId: string,
) {
  isDeleting.value = true

  try {
    await $httpFetch(sellerProfileAPI.deleteTaxIdentificationNumber, {
      pathParams: { tinId: taxIdentificationNumberId },
    })

    refresh()

    openSuccessToast({
      title: i18n(translations.taxIdentificationNumberDeleteSuccessTitle),
      content: i18n(translations.taxIdentificationNumberDeleteSuccessContent),
    })
  } catch (deletionError) {
    openErrorToast({
      content: getApiErrorMessage(deletionError as Error),
    })
  } finally {
    isDeleting.value = false
  }
}
</script>
